
import React, {useState, useContext, useEffect, useRef} from 'react'
import {
	Button,
	Card,
	CardBody, CardHeader,
	Col, Collapse,
	Form,
	FormGroup,
	Input,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Row
} from 'reactstrap'
import { useTranslation } from 'react-i18next'
import { SpinnerComponent } from 'react-element-spinner'

import { trigger } from '../../events/customEvents'
import OemsService from '../../services/OemsService'
import SiteService from '../../services/SiteService'
import { SessionContext } from '../../contexts/SessionProvider'
import DealersService from '../../services/DealersService'
import UsersService from '../../services/UsersService'

const SelectDealerUserModalRedux = ({ showDealerUserModal = false, exclusionList = [], activeTierMembers = [], tierId = 0 }) =>
{
	const [isModalBusy, setIsModalBusy] = useState(false)
	const sessionContext = useContext(SessionContext)
	const [oems, setOems] = useState([])
	const [openedOemCollapse, setOpenedOemCollapse] = useState([])
	const [activeOemIndex, setActiveOemIndex] = useState(-1)
	const { t } = useTranslation()
	const [openedDealerCollapse, setOpenedDealerCollapse] = useState([])
	const [activeDealerIndex, setActiveDealerIndex] = useState(-1)
	const stateRef = useRef()
	
	stateRef.activeOemIndex = activeOemIndex
	stateRef.activeDealerIndex = activeDealerIndex
	
	const handleOnOpened = () =>
	{
		retrieveOems()
	}
	
	const retrieveOems = () =>
	{
		setIsModalBusy(true)
		OemsService
			.getOems(sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsModalBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				setOems(result.data.actionResult)
			})
			.catch((error) =>
			{
				setIsModalBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const toggleOemCollapse = (oemIndex) =>
	{
		if (openedOemCollapse.includes(oemIndex))
		{
			setOpenedOemCollapse([])
			setActiveOemIndex(-1)
		}
		else
		{
			setOpenedOemCollapse([oemIndex])
			setActiveOemIndex(oemIndex)
			
			const oemsClone = [...oems]
			if (oemsClone[oemIndex].dealers.length === 0)
			{
				retrieveDealers(oemIndex, false)
			}
		}
	}
	
	const retrieveDealers = (oemIndex, withUsers) =>
	{
		let oemsClone = [...oems]
		
		setIsModalBusy(true)
		DealersService
			.getOemDealers(oemsClone[oemIndex].oemId, withUsers, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsModalBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				// When users are loaded in bulk they can also be returned once per auction role,
				// so de-duplicate each dealer's users by entityId to avoid listing anyone twice.
				const dealers = result.data.actionResult
				if (withUsers)
				{
					dealers.forEach((dealer) =>
					{
						if (Array.isArray(dealer.dealerUsers))
						{
							const seenEntityIds = new Set()
							dealer.dealerUsers = dealer.dealerUsers.filter((dealerUser) =>
							{
								if (seenEntityIds.has(dealerUser.entityId))
								{
									return false
								}
								seenEntityIds.add(dealerUser.entityId)
								return true
							})
						}
					})
				}
				
				oemsClone[oemIndex].dealers = dealers
				oemsClone[oemIndex].hasAllUsers = withUsers
				setOems(oemsClone)
			})
			.catch((error) =>
			{
				setIsModalBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const toggleDealerCollapse = (dealerIndex) =>
	{
		if (openedDealerCollapse.includes(dealerIndex))
		{
			setOpenedDealerCollapse([])
			setActiveDealerIndex(-1)
		}
		else
		{
			setOpenedDealerCollapse([dealerIndex])
			setActiveDealerIndex(dealerIndex)
			
			let oemsClone = [...oems]
			if (oemsClone[activeOemIndex].dealers[dealerIndex].dealerUsers.length === 0)
			{
				retrieveFilteredDealerUsers(dealerIndex)
			}
		}
	}
	
	const retrieveFilteredDealerUsers = (dealerIndex) =>
	{
		setIsModalBusy(true)
		UsersService
			.getFilteredDealerUsers(exclusionList, oems[activeOemIndex].dealers[dealerIndex].dealerId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsModalBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				// A dealer user who holds more than one auction role (e.g. both AuctionBuyer
				// and AuctionViewer) is returned once per role, so the same person can appear
				// multiple times. De-duplicate by entityId so the picker only lists each user
				// once.
				const seenEntityIds = new Set()
				const uniqueDealerUsers = result.data.actionResult.filter((dealerUser) =>
				{
					if (seenEntityIds.has(dealerUser.entityId))
					{
						return false
					}
					seenEntityIds.add(dealerUser.entityId)
					return true
				})
				
				uniqueDealerUsers.forEach(externalUser =>
				{
					externalUser.tierId = tierId
				})
				
				let oemsClone = [...oems]
				oemsClone[activeOemIndex].dealers[dealerIndex].dealerUsers = uniqueDealerUsers
				setOems(oemsClone)
			})
			.catch((error) =>
			{
				setIsModalBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const handleCheckboxChange = (selected, i) =>
	{
		let oemsClone = [...oems]
		oemsClone[activeOemIndex].dealers[activeDealerIndex].dealerUsers[i].selected = !selected
		setOems(oemsClone)
	}
	
	const handleSelectAll = () =>
	{
		let oemsClone = [...oems]
		oemsClone[activeOemIndex].dealers[activeDealerIndex].dealerUsers.forEach((dealerUser) => { dealerUser.selected = true })
		setOems(oemsClone)
	}
	
	const handleUnselectAll = () =>
	{
		let oemsClone = [...oems]
		oemsClone[activeOemIndex].dealers[activeDealerIndex].dealerUsers.forEach((dealerUser) => { dealerUser.selected = false })
		setOems(oemsClone)
	}
	
	const handleSelectAllDealerUsers = () =>
	{
		if (oems[activeOemIndex].hasAllUsers)
		{
			if (activeDealerIndex > -1)
			{
				toggleDealerCollapse(activeDealerIndex)
				setActiveDealerIndex(-1)
			}
			
			let oemsClone = [...oems]
			oemsClone[activeOemIndex].dealers = []
			setOems(oemsClone)
			
			retrieveDealers(activeOemIndex,true)
		}
		else
		{
			let oemsClone = [...oems]
			oemsClone[activeOemIndex].dealers.forEach((dealer) =>
			{
				dealer.dealerUsers.forEach((dealerUser) => { dealerUser.selected = true })
			})
			setOems(oemsClone)
		}
	}
	
	const handleUnselectAllDealerUsers = () =>
	{
		let oemsClone = [...oems]
		oemsClone[activeOemIndex].dealers.forEach((dealer) =>
		{
			dealer.dealerUsers.forEach((dealerUser) => { dealerUser.selected = false })
		})
		setOems(oemsClone)
	}
	
	const handleCancelModal = () =>
	{
		trigger('selectDealerUserModalClosed', { activeTierMembers: activeTierMembers, tierId: tierId })
	}
	
	const handleSaveSelectedUsers = () =>
	{
		let activeTierMembersClone = [...activeTierMembers]
		
		let selectedDealerUsers = []
		let oemsClone = [...oems]
		oemsClone.forEach((oem) =>
		{
			oem.dealers.forEach((dealer) =>
			{
				const selectedUsers = dealer.dealerUsers.filter((dealerUser) => dealerUser.selected === true)
				selectedDealerUsers = selectedDealerUsers.concat(selectedUsers)
			})
		})
		
		// De-duplicate by entityId: a user can belong to more than one dealer (or already
		// be a tier member), so guard against adding the same person twice — that produced
		// duplicate tier members and duplicate auction emails.
		const existingEntityIds = new Set(activeTierMembersClone.map((tierMember) => tierMember.entityId))
		selectedDealerUsers = selectedDealerUsers.filter((dealerUser) =>
		{
			if (existingEntityIds.has(dealerUser.entityId))
			{
				return false
			}
			existingEntityIds.add(dealerUser.entityId)
			return true
		})
		
		if (selectedDealerUsers.length > 0)
		{
			let nextTierMemberId = 1
			if (activeTierMembersClone.length > 0)
			{
				const adminAuctionTierMember = activeTierMembersClone.reduce((prev, current) => (prev && prev.tierMemberId > current.tierMemberId) ? prev : current)
				nextTierMemberId = adminAuctionTierMember.tierMemberId + 1
			}
			
			selectedDealerUsers.forEach(dealerUser =>
			{
				dealerUser.tierMemberId = nextTierMemberId
				nextTierMemberId++
			})
			
			activeTierMembersClone = activeTierMembersClone.concat(selectedDealerUsers)
		}
		
		trigger('selectDealerUserModalClosed', { activeTierMembers: activeTierMembersClone, tierId: tierId })
	}
	
	return (
		<Modal
			size="lg"
			isOpen={showDealerUserModal}
			onOpened={handleOnOpened}
		>
			<ModalHeader toggle={() => handleCancelModal()} charCode="Y">
				{ t('Modals.SelectDealerUser.Title') }
			</ModalHeader>
			<ModalBody>
				<Card>
					<CardBody>
						<div className="accordion">
							{
								oems.map((oem, oemIndex) => (
									<Card
										className="card-plain"
										key={ oem.oemId }
									>
										<CardHeader role="tab">
											<Button
												block
												color="link"
												className="text-start m-0 p-0"
												onClick={() => toggleOemCollapse(oemIndex)}
												aria-expanded={openedOemCollapse.includes(
													oemIndex
												)}
											>
												<h5 className="m-0 p-0" style={{ fontSize: '12px', fontWeight: 'bolder' }}>{ oem.name }</h5>
											</Button>
										</CardHeader>
										<Collapse
											role="tabpanel"
											isOpen={openedOemCollapse.includes(oemIndex)}
										>
											<CardBody>
												<div className="accordion">
													{activeOemIndex > -1 &&
														<Row style={{ paddingBottom: '2px' }}>
															<Col md="12">
																<div className="text-right" style={{ paddingBottom:'10px' }}>
																	<Button style={{ marginLeft: '5px' }} color="secondary" size="sm" onClick={ () => handleSelectAllDealerUsers() }>
																		{ t('Modals.SelectDealerUser.Select_All_Users') }
																	</Button>
																	<Button style={{ marginLeft: '5px' }} color="secondary" size="sm" onClick={ () => handleUnselectAllDealerUsers() }>
																		{ t('Modals.SelectDealerUser.Unselect_All_Users') }
																	</Button>
																</div>
															</Col>
														</Row>
													}
													{activeOemIndex > -1 && oems[activeOemIndex].dealers.map((dealer, dealerIndex) => (
														<Card
															className="card-plain"
															key={ dealer.dealerId }
														>
															<CardHeader role="tab">
																<Button
																	block
																	color="link"
																	className="text-start m-0 p-0"
																	onClick={() => toggleDealerCollapse(dealerIndex)}
																	aria-expanded={openedDealerCollapse.includes(
																		dealerIndex
																	)}
																>
																	<h5 className="m-0 p-0" style={{ fontSize: '12px', fontWeight: 'bolder' }}>{ dealer.name }</h5>
																</Button>
															</CardHeader>
															<Collapse
																role="tabpanel"
																isOpen={openedDealerCollapse.includes(dealerIndex)}
															>
																<CardBody>
																	<Row>
																		<Col md="12" style={{ textAlign: 'center' }}>
																			{activeDealerIndex > -1 && oems[activeOemIndex].dealers[activeDealerIndex].dealerUsers.length === 0 &&
																				<label><small>{ t('Modals.SelectDealerUser.No_Selectable_Users') }</small></label>
																			}
																		</Col>
																	</Row>
																	<Row style={{ paddingBottom: '2px' }}>
																		<Col md="12">
																			<div className="text-right">
																				{activeDealerIndex > -1 && oems[activeOemIndex].dealers[activeDealerIndex].dealerUsers.length > 0 &&
																					<>
																						<Button style={{ marginLeft: '5px' }} color="secondary" size="sm" onClick={ () => handleSelectAll() }>
																							{ t('Modals.SelectDealerUser.Select_All') }
																						</Button>
																						<Button style={{ marginLeft: '5px' }} color="secondary" size="sm" onClick={ () => handleUnselectAll() }>
																							{ t('Modals.SelectDealerUser.Unselect_All') }
																						</Button>
																					</>
																				}
																			</div>
																		</Col>
																	</Row>
																	<Row>
																		<Col md="12">
																			<div className="tier-group">
																				{activeDealerIndex > -1 &&
																					oems[activeOemIndex].dealers[activeDealerIndex].dealerUsers.map((dealerUser, i) => (
																						<FormGroup key={ i }>
																							<input
																								id={ i }
																								type="checkbox"
																								checked={ dealerUser.selected }
																								onChange={() => handleCheckboxChange(dealerUser.selected, i)}
																							/>
																							<label
																								className="form-control-label"
																								style={{ marginLeft: '3px' }}
																								htmlFor={ i }
																							>
																								{ dealerUser.fullName }
																							</label>
																						</FormGroup>
																					))
																				}
																			</div>
																		</Col>
																	</Row>
																</CardBody>
															</Collapse>
														</Card>
													))
													}
												</div>
											</CardBody>
										</Collapse>
									</Card>
								))
							}
						</div>
						<div className="text-center">
							<Button
								className="my-4"
								color="secondary"
								type="button"
								size="sm"
								onClick={ () => handleCancelModal() }
								style={{ marginRight: '5px' }}
							>
								{ t('Modals.SelectExternalUser.Cancel') }
							</Button>
							<Button
								className="my-4"
								color="primary"
								type="button"
								size="sm"
								onClick={ () => handleSaveSelectedUsers() }
							>
								{ t('Modals.SelectExternalUser.Save') }
							</Button>
						</div>
					</CardBody>
				</Card>
			</ModalBody>
			<ModalFooter></ModalFooter>
		</Modal>
	)
}

export default SelectDealerUserModalRedux
